import { useTranslation } from "@redotech/redo-i18n";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { DisabledTooltip } from "@redotech/redo-web/arbiter-components/tooltip/disabled-tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useState } from "react";
import { FieldTranslationsModal } from "./field-translations-modal";
import { FlowTranslatableFieldSpec } from "./translation-save-diff";
import { useFlowTextTranslationsEnabled } from "./use-flow-text-translations-enabled";

/**
 * "View translations" caption link under a flow text field. Disabled with an
 * explanatory tooltip until the flow is saved and the field has text.
 */
export const FlowFieldTranslations = memo(function FlowFieldTranslations({
  elementId,
  stepElementIds,
  field,
  flowType,
  canEdit,
}: {
  /** The element's stable uuid; absent on drafts saved before ids existed. */
  elementId: string | undefined;
  /** Every element id on the step, for the step-scoped translations query. */
  stepElementIds: readonly string[];
  field: FlowTranslatableFieldSpec;
  flowType: FlowType;
  canEdit: boolean;
}) {
  const enabled = useFlowTextTranslationsEnabled();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!enabled) {
    return null;
  }
  const disabledReason = !elementId
    ? t("Save the flow to enable translations.")
    : field.source.trim() === ""
      ? t("Add text to this field to enable translations.")
      : undefined;
  return (
    <Flex align="flex-start">
      <DisabledTooltip disabledMessage={disabledReason}>
        <RedoButton
          disabled={disabledReason !== undefined}
          hierarchy="link_underline"
          onClick={() => setOpen(true)}
          size="xs"
          text={t("View translations")}
        />
      </DisabledTooltip>
      {open && elementId && (
        <FieldTranslationsModal
          canEdit={canEdit}
          elementId={elementId}
          field={field}
          flowType={flowType}
          onClose={() => setOpen(false)}
          stepElementIds={stepElementIds}
        />
      )}
    </Flex>
  );
});
